import { create } from 'zustand';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

// Matches @brutal/deployment-schemas DeploymentStatusSchema
export type DeploymentStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'rolled_back';

export interface Deployment {
  id: string;
  artifact: string;
  version: string;
  environment: string;
  status: DeploymentStatus;
  startedAt: string;
  completedAt: string | null;
  error: string | null;
}

// Pipeline execution types matching @brutal/pipeline-schemas
export type PipelineExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface StepResult {
  stepId: string;
  status: 'completed' | 'failed' | 'skipped';
  output: string | null;
  error: string | null;
  duration: number;
  retries: number;
}

export interface PipelineExecution {
  executionId: string;
  pipelineName: string;
  status: PipelineExecutionStatus;
  trigger: string;
  startedAt: string;
  completedAt: string | null;
  stepResults: StepResult[];
  totalDuration: number | null;
  error: string | null;
}

export interface Artifact {
  name: string;
  type: string;
  latestVersion: string | null;
  environments: Record<string, { version: string; status: string } | null>;
}

// Approval status type matching @brutal/agent-schemas
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';

export interface ApprovalRequest {
  id: string;
  deploymentId: string;
  artifact: string;
  version: string;
  environment: string;
  requestedBy: string;
  createdAt: string;
  expiresAt: string | null;
  status: ApprovalStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewComment: string | null;
  reason: string | null;
  stageName: string | null;
  isBreakGlass: boolean;
}

export type NotificationType = 'approval_requested' | 'approval_approved' | 'approval_rejected';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  timestamp: number;
}

export interface DashboardState {
  // Connection
  connectionStatus: ConnectionStatus;
  setConnectionStatus: (status: ConnectionStatus) => void;

  // Deployments
  deployments: Deployment[];
  setDeployments: (deployments: Deployment[]) => void;
  addDeployment: (deployment: Deployment) => void;
  updateDeployment: (id: string, updates: Partial<Deployment>) => void;

  // Pipelines
  pipelines: PipelineExecution[];
  setPipelines: (pipelines: PipelineExecution[]) => void;
  addPipeline: (pipeline: PipelineExecution) => void;
  updatePipeline: (executionId: string, updates: Partial<PipelineExecution>) => void;

  // Artifacts
  artifacts: Artifact[];
  setArtifacts: (artifacts: Artifact[]) => void;

  // Selection
  selectedArtifact: string | null;
  selectedEnvironment: string | null;
  setSelectedArtifact: (artifact: string | null) => void;
  setSelectedEnvironment: (environment: string | null) => void;

  // Approvals
  approvals: ApprovalRequest[];
  setApprovals: (approvals: ApprovalRequest[]) => void;
  addApproval: (approval: ApprovalRequest) => void;
  updateApproval: (id: string, updates: Partial<ApprovalRequest>) => void;
  removeApproval: (id: string) => void;

  // Notifications
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
}

export const useDashboardStore = create<DashboardState>((set) => ({
  // Connection
  connectionStatus: 'disconnected',
  setConnectionStatus: (status) => set({ connectionStatus: status }),

  // Deployments
  deployments: [],
  setDeployments: (deployments) => set({ deployments }),
  addDeployment: (deployment) =>
    set((state) => ({ deployments: [deployment, ...state.deployments] })),
  updateDeployment: (id, updates) =>
    set((state) => ({
      deployments: state.deployments.map((d) => (d.id === id ? { ...d, ...updates } : d)),
    })),

  // Pipelines
  pipelines: [],
  setPipelines: (pipelines) => set({ pipelines }),
  addPipeline: (pipeline) => set((state) => ({ pipelines: [pipeline, ...state.pipelines] })),
  updatePipeline: (executionId, updates) =>
    set((state) => ({
      pipelines: state.pipelines.map((p) =>
        p.executionId === executionId ? { ...p, ...updates } : p,
      ),
    })),

  // Artifacts
  artifacts: [],
  setArtifacts: (artifacts) => set({ artifacts }),

  // Selection
  selectedArtifact: null,
  selectedEnvironment: null,
  setSelectedArtifact: (artifact) => set({ selectedArtifact: artifact }),
  setSelectedEnvironment: (environment) => set({ selectedEnvironment: environment }),

  // Approvals
  approvals: [],
  setApprovals: (approvals) => set({ approvals }),
  addApproval: (approval) => set((state) => ({ approvals: [approval, ...state.approvals] })),
  updateApproval: (id, updates) =>
    set((state) => ({
      approvals: state.approvals.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    })),
  removeApproval: (id) =>
    set((state) => ({
      approvals: state.approvals.filter((a) => a.id !== id),
    })),

  // Notifications
  notifications: [],
  addNotification: (notification) =>
    set((state) => ({
      notifications: [
        {
          ...notification,
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          timestamp: Date.now(),
        },
        ...state.notifications,
      ].slice(0, 5), // Keep max 5 notifications
    })),
  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),
}));
